import { FormTheme } from '../../types';

export const TEMPLATE_CONFIG_TYPE = 'form-builder-template';
export const TEMPLATE_CONFIG_VERSION = 1;

export interface FormTemplate {
  id: string;
  name: string;
  description: string;
  formType: 'single' | 'multistep';
  targetDevice: 'mobile' | 'tablet' | 'desktop';
  configuration: Record<string, any> | any[];
  steps?: string[];
  theme?: FormTheme | null;
  visibility?: 'global' | 'workspace' | 'private';
}

export interface TemplateConfig {
  type: typeof TEMPLATE_CONFIG_TYPE;
  version: number;
  name: string;
  description: string;
  formType: FormTemplate['formType'];
  targetDevice: FormTemplate['targetDevice'];
  steps: string[];
  theme: FormTheme | null;
  configuration: FormTemplate['configuration'];
}

export const buildTemplateConfig = (template: FormTemplate): string => {
  const config: TemplateConfig = {
    type: TEMPLATE_CONFIG_TYPE,
    version: TEMPLATE_CONFIG_VERSION,
    name: template.name,
    description: template.description || '',
    formType: template.formType,
    targetDevice: template.targetDevice,
    steps: template.steps || [],
    theme: template.theme ?? null,
    configuration: template.configuration || {},
  };
  return JSON.stringify(config, null, 2);
};

const FORM_TYPES: FormTemplate['formType'][] = ['single', 'multistep'];
const DEVICES: FormTemplate['targetDevice'][] = ['mobile', 'tablet', 'desktop'];

export type ParseResult = { template: FormTemplate; error?: undefined } | { template?: undefined; error: string };

export const parseTemplateConfig = (
  text: string,
  fallback: Pick<FormTemplate, 'formType' | 'targetDevice'>,
): ParseResult => {
  if (!text.trim()) return { error: 'Paste a template config first.' };

  let parsed: any;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { error: 'This is not valid JSON. Copy the config again and paste the whole text.' };
  }

  const source = Array.isArray(parsed) ? { configuration: parsed } : parsed;
  if (!source || typeof source !== 'object' || !source.configuration) {
    return { error: 'No fields found. The config needs a "configuration" section.' };
  }
  if (typeof source.configuration !== 'object') {
    return { error: '"configuration" must be a list or object of fields.' };
  }

  const formType = FORM_TYPES.includes(source.formType) ? source.formType : fallback.formType;
  const steps = Array.isArray(source.steps) ? source.steps.map(String) : [];

  return {
    template: {
      id: 'pasted',
      name: String(source.name || 'Pasted template'),
      description: String(source.description || ''),
      formType,
      targetDevice: DEVICES.includes(source.targetDevice) ? source.targetDevice : fallback.targetDevice,
      configuration: source.configuration,
      steps: formType === 'multistep' && steps.length === 0 ? ['Step 1'] : steps,
      theme: source.theme && typeof source.theme === 'object' ? source.theme : null,
    },
  };
};

export const countTemplateFields = (configuration: FormTemplate['configuration'] | undefined) =>
  configuration ? Object.keys(configuration).length : 0;
